// components/navbar/NavSearch.js
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import useSearch from "../../hooks/useSearch"; // Adjust path as needed

const NavSearch = ({ onExpandedChange }) => {
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const searchInputRef = useRef(null);
  const searchDropdownRef = useRef(null);
  const navigate = useNavigate();
  
  const {
    query: searchQuery,
    setQuery: setSearchQuery,
    results: liveResults,
    isSearching,
    hasResults,
    clearResults
  } = useSearch();

  // Update parent component about expanded state
  useEffect(() => {
    if (onExpandedChange) {
      onExpandedChange(isSearchExpanded);
    }
  }, [isSearchExpanded, onExpandedChange]);

  // Auto-focus input when expanded
  useEffect(() => {
    if (isSearchExpanded && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isSearchExpanded]);

  // Handle click outside to close search
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isSearchExpanded) {
        const searchForm = document.querySelector('.navbar-search-form');
        const searchToggle = searchForm?.querySelector('.search-toggle');
        
        // Check if click is outside search elements
        const isOutsideSearch = 
          !searchInputRef.current?.contains(event.target) &&
          !searchDropdownRef.current?.contains(event.target) &&
          !searchForm?.contains(event.target) &&
          event.target !== searchToggle &&
          !searchToggle?.contains(event.target);
        
        if (isOutsideSearch) {
          setIsSearchExpanded(false);
          clearResults();
        }
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSearchExpanded, clearResults]);

  // Handle escape key
  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === 'Escape' && isSearchExpanded) {
        setIsSearchExpanded(false);
        clearResults();
      }
    };
    
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('keydown', handleEsc);
    };
  }, [isSearchExpanded, clearResults]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?query=${encodeURIComponent(searchQuery)}`);
      setIsSearchExpanded(false);
      setSearchQuery("");
    }
  };

  // Close search handler for mobile
  const handleCloseSearch = () => {
    setIsSearchExpanded(false);
    setSearchQuery("");
  };

  return (
    <div className="navbar-search">
      <form
        className={`navbar-search-form ${isSearchExpanded ? "expanded" : ""}`}
        onSubmit={handleSearchSubmit}
      >
        {/* Using a div wrapper for better positioning */}
        <div className="search-icon-wrapper" style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: isSearchExpanded ? '40px' : '100%',
          position: 'relative',
          height: '100%',
        }}>
          <button
            type="button"
            className="search-toggle"
            aria-label="Search"
            onClick={(e) => {
              e.preventDefault();
              setIsSearchExpanded(!isSearchExpanded);
              if (!isSearchExpanded) {
                setTimeout(() => {
                  searchInputRef.current?.focus();
                }, 100);
              }
            }}
            style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              position: 'relative',
            }}
          >
            <svg
              className="search-icon"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{
                width: '20px',
                height: '20px',
                position: isSearchExpanded ? 'absolute' : 'static',
                left: isSearchExpanded ? '10px' : 'auto',
                top: isSearchExpanded ? '50%' : 'auto',
                transform: isSearchExpanded ? 'translateY(-50%)' : 'none'
              }}
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          </button>
        </div>
        
        <input
          ref={searchInputRef}
          type="text"
          placeholder="Search anime or characters..."
          className="navbar-search-input"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          aria-label="Search anime or characters"
        />

        {/* Close button - visible on mobile when expanded */}
        {isSearchExpanded && (
          <button
            type="button"
            className="search-close-btn"
            aria-label="Close search"
            onClick={handleCloseSearch}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '8px',
              position: 'absolute',
              right: '5px',
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 2
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              width="16"
              height="16"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        )}

        {/* Loading spinner - positioned absolutely */}
        {isSearching && (
          <div className="live-search-loading" style={{
            position: 'absolute',
            right: '1rem',
            top: '50%',
            transform: 'translateY(-50%)',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            zIndex: 2
          }}>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ animation: "spin 1s linear infinite" }}
            >
              <path d="M21 12a9 9 0 1 1-6.219-8.56" />
            </svg>
          </div>
        )}
      </form>

      {/* Live results dropdown */}
      {isSearchExpanded && hasResults && (
        <div
          className="navbar-search-dropdown"
          ref={searchDropdownRef}
          onMouseDown={(e) => e.preventDefault()} // Prevent input blur when clicking dropdown
        >
          {/* Anime Results */}
          {liveResults.anime.length > 0 && (
            <>
              <div className="navbar-search-category">Anime</div>
              {liveResults.anime.map((item) => (
                <div
                  key={`anime-${item.mal_id || item.id}`}
                  className="navbar-search-item"
                  onClick={() => {
                    navigate(`/anime/${item.id}`);
                    setIsSearchExpanded(false);
                    setSearchQuery("");
                  }}
                  role="button"
                  tabIndex="0"
                  aria-label={`Go to ${item.title} page`}
                >
                  <div className="search-item-thumbnail">
                    <img
                      src={
                        item.images?.jpg?.small_image_url ||
                        "https://via.placeholder.com/50"
                      }
                      alt={item.title}
                      loading="lazy"
                    />
                  </div>
                  <div className="search-item-details">
                    <div className="search-item-title">{item.titleEnglish || item.title}</div>
                    <div className="search-item-type">
                      {item.type || "Anime"}
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}

          {/* Manga Results - New addition from HomeSearch */}
          {liveResults.manga?.length > 0 && (
            <>
              <div className="navbar-search-category">Manga</div>
              {liveResults.manga.map((item) => (
                <div
                  key={`manga-${item.mal_id || item.id}`}
                  className="navbar-search-item"
                  onClick={() => {
                    navigate(`/manga/${item.id}`);
                    setIsSearchExpanded(false);
                    setSearchQuery("");
                  }}
                  role="button"
                  tabIndex="0"
                  aria-label={`Go to ${item.title} page`}
                >
                  <div className="search-item-thumbnail">
                    <img
                      src={
                        item.images?.jpg?.small_image_url ||
                        "https://via.placeholder.com/50"
                      }
                      alt={item.title}
                      loading="lazy"
                    />
                  </div>
                  <div className="search-item-details">
                    <div className="search-item-title">{item.titleEnglish || item.title}</div>
                    <div className="search-item-type">
                      {item.type || "Manga"}
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}

          {/* Character Results */}
          {liveResults.characters.length > 0 && (
            <>
              <div className="navbar-search-category">Characters</div>
              {liveResults.characters.map((character) => (
                <div
                  key={`character-${character.mal_id || character.id}`}
                  className="navbar-search-item"
                  onClick={() => {
                    navigate(`/characters/${character._id}`);
                    setIsSearchExpanded(false);
                    setSearchQuery("");
                  }}
                  role="button"
                  tabIndex="0"
                  aria-label={`Go to ${character.name} page`}
                >
                  <div className="search-item-thumbnail">
                    <img
                      src={
                        character.images?.webp?.image_url ||
                        "https://via.placeholder.com/50"
                      }
                      alt={character.name}
                      loading="lazy"
                    />
                  </div>
                  <div className="search-item-details">
                    <div className="search-item-title">
                      {character.name}
                    </div>
                    <div className="search-item-type">Character</div>
                  </div>
                </div>
              ))}
            </>
          )}
          
          {/* View All Results Button - Added from HomeSearch */}
          {searchQuery.trim() && (
            <div className="view-all-results">
              <button 
                onClick={handleSearchSubmit}
                className="view-all-button"
              >
                View All Results for "{searchQuery}"
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NavSearch;